/**
 * SegmentedControl — pick ONE of 2–4 mutually-exclusive options that
 * reconfigure the current view IN PLACE, no navigation (design.md §5):
 * view mode (Liste/Board, Diagramm/Liste) and scope/range (Eingang/Ausgang,
 * 30 Tage/12 Monate). Distinct from underline <Tabs variant="underline">,
 * which switches content sections within one entity — do not mix the patterns.
 *
 * Styling per contract: track = --secondary fill + 1px --border, radius 8px
 * (rounded-lg), 2px padding; active segment = white --card pill + subtle
 * shadow, radius 6px (rounded-md).
 *
 * Icon-vs-text is itself the signal: icon-only = HOW it's shown (only with
 * conventional icons: Liste/Board/Grid/Diagramm — `iconOnly`, option.label
 * becomes the segment's aria-label), text = WHICH slice (optional trailing
 * count, e.g. "Eingang 5").
 *
 * Guardrail: 2–4 options. At 5+ or a custom range use a Select instead
 * (dev-time console.warn).
 */
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface SegmentedControlOption {
  value: string;
  /** Visible text segment label — or, with `iconOnly`, the segment's aria-label. */
  label: string;
  icon?: ReactNode;
  /** Optional trailing count for text segments (e.g. "Eingang 5"). */
  count?: number | string;
}

export interface SegmentedControlProps {
  value: string;
  onValueChange: (value: string) => void;
  options: SegmentedControlOption[];
  /** Accessible name of the radiogroup (e.g. "Ansicht", "Zeitraum"). */
  ariaLabel: string;
  /** Render icons only; each option's `label` becomes its aria-label. */
  iconOnly?: boolean;
  className?: string;
}

export function SegmentedControl({
  value,
  onValueChange,
  options,
  ariaLabel,
  iconOnly = false,
  className,
}: SegmentedControlProps) {
  if (import.meta.env.DEV && (options.length < 2 || options.length > 4)) {
    // eslint-disable-next-line no-console
    console.warn(
      `SegmentedControl: ${options.length} options, but the contract is 2 to 4 (design.md §5). Use a Select for 5+ or custom ranges.`,
    );
  }
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex h-8 items-center rounded-lg border border-border bg-secondary p-0.5',
        className,
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            role="radio"
            aria-checked={active}
            aria-label={iconOnly ? opt.label : undefined}
            type="button"
            onClick={() => onValueChange(opt.value)}
            className={cn(
              'inline-flex h-7 items-center gap-1.5 whitespace-nowrap rounded-md text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              iconOnly ? 'px-2' : 'px-2.5',
              active
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {opt.icon}
            {!iconOnly && opt.label}
            {!iconOnly && opt.count !== undefined && <span className="opacity-70">{opt.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
